import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { identityApiRef, useApi } from '@backstage/frontend-plugin-api';
import {
  catalogApiRef,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';
import type { Entity } from '@backstage/catalog-model';
import {
  Page,
  Header,
  Content,
  Progress,
  ResponseErrorPanel,
  EmptyState,
  Link,
} from '@backstage/core-components';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  makeStyles,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { fullRef, shortRef, str } from '../vocabulary/refs';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';

const useStyles = makeStyles(theme => ({
  /* --- Filter bar: horizontal, above the results --- */
  filterBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
    alignItems: 'center',
    marginBottom: 20,
  },
  search: { flex: '1 1 260px', minWidth: 220 },
  select: { width: 160 },
  count: {
    marginLeft: 'auto',
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    whiteSpace: 'nowrap',
  },

  /* --- Cards --- */
  cardLink: { display: 'block', height: '100%', textDecoration: 'none' },
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'border-color 120ms ease, background-color 120ms ease',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: 'var(--ar-surface-2)',
    },
  },
  cardBody: { flex: 1, display: 'flex', flexDirection: 'column', gap: 6 },
  kindRow: {
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--ar-fg-3)',
  },
  /* Card is wrapped in a link, so the star sits above it and stops the click. */
  cardWrap: { position: 'relative', height: '100%' },
  star: {
    position: 'absolute',
    top: 6,
    right: 6,
    zIndex: 1,
    padding: 6,
    color: 'var(--ar-fg-3)',
    '&:hover': { color: '#e3b341' },
  },
  starOn: { color: '#e3b341' },
  name: {
    fontSize: '0.9375rem',
    fontWeight: 620,
    letterSpacing: '-0.011em',
    color: theme.palette.primary.main,
  },
  desc: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    color: 'var(--ar-fg-2)',
    fontSize: '0.8125rem',
  },
  meta: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
    marginTop: 'auto',
    paddingTop: 12,
  },
  /* Footer: what it is part of (system) and who owns it (team). */
  footer: {
    display: 'flex',
    gap: 18,
    marginTop: 12,
    paddingTop: 10,
    borderTop: '1px solid var(--ar-border-soft)',
  },
  footerCell: { minWidth: 0 },
  footerLabel: {
    display: 'block',
    fontSize: '0.625rem',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--ar-fg-3)',
  },
  footerValue: {
    display: 'block',
    fontSize: '0.8125rem',
    color: 'var(--ar-fg-2)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

const ANY = '__any__';
const MINE = '__mine__';
const STARRED = '__starred__';

/**
 * What this page is a catalog OF: the things we build and run, and the systems
 * they make up. Clients are not here: CustomerIQ owns them (D-33).
 *
 * This is a query filter, not just a display one — the page used to fetch every
 * kind and derive the Kind dropdown from whatever came back, which offered
 * User, Group and Location as dead ends (Location is registration plumbing, and
 * there is no people page for the other two). It also meant the payload grew
 * with headcount rather than with the catalog: once GitHub org ingestion lands,
 * every employee is a User entity this page would have downloaded.
 */
const BROWSABLE_KINDS = ['Component', 'API', 'Resource', 'System'];

/**
 * Catalog browse with the filters in a bar ABOVE the results, and the results
 * as a grid of cards rather than a data table.
 */
export const BrowsePage = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);
  const { toggleStarredEntity, isStarredEntity } = useStarredEntities();

  const [entities, setEntities] = useState<Entity[]>();
  const [error, setError] = useState<Error>();
  // The user's own ref plus every group they belong to — i.e. "me or my team".
  const [myRefs, setMyRefs] = useState<string[]>([]);

  // `?owner=` is how an owner REFERENCE resolves now that teams have no page of
  // their own — ../people redirects `/catalog/:ns/group/:name` here. Read once,
  // as the initial value, rather than kept in sync: the URL is an entry point,
  // and a filter the visitor then changes by hand should not be fighting it.
  //
  // "All kinds" rather than the usual Component default, because arriving from
  // an owner ref is a question about everything that team owns, and answering
  // it with only the Components would silently hide the rest.
  const [searchParams] = useSearchParams();
  const ownerParam = searchParams.get('owner');

  const [query, setQuery] = useState('');
  const [kind, setKind] = useState(ownerParam ? ANY : 'Component');
  const [type, setType] = useState(ANY);
  const [lifecycle, setLifecycle] = useState(ANY);
  const [owner, setOwner] = useState(ownerParam ?? ANY);
  const [scope, setScope] = useState(ANY);

  useEffect(() => {
    let active = true;
    catalogApi
      .getEntities({
        filter: { kind: BROWSABLE_KINDS },
        fields: [
          'kind',
          'metadata.name',
          'metadata.namespace',
          'metadata.title',
          'metadata.description',
          'spec.type',
          'spec.lifecycle',
          'spec.owner',
          'spec.system',
        ],
      })
      .then(res => active && setEntities(res.items))
      .catch(e => active && setError(e as Error));
    return () => {
      active = false;
    };
  }, [catalogApi]);

  useEffect(() => {
    let active = true;
    identityApi
      .getBackstageIdentity()
      .then(id => active && setMyRefs(id.ownershipEntityRefs ?? []))
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [identityApi]);

  const all = useMemo(() => entities ?? [], [entities]);

  const kinds = useMemo(
    () => Array.from(new Set(all.map(e => e.kind))).sort(),
    [all],
  );

  // Options reflect the selected kind, so they never offer dead ends. With
  // "All kinds" the pool is everything, and type/lifecycle/owner then span
  // kinds — a Component's "service" and an API's "openapi" sit in one list,
  // which is the point of looking across kinds in the first place.
  const ofKind = useMemo(
    () => (kind === ANY ? all : all.filter(e => e.kind === kind)),
    [all, kind],
  );
  const uniq = (vals: string[]) =>
    Array.from(new Set(vals.filter(Boolean))).sort();
  const types = useMemo(
    () => uniq(ofKind.map(e => str(e.spec?.type))),
    [ofKind],
  );
  const lifecycles = useMemo(
    () => uniq(ofKind.map(e => str(e.spec?.lifecycle))),
    [ofKind],
  );
  // A team arriving from an owner ref may own nothing in the current pool, and
  // a Select whose value is not among its options renders blank and warns. Keep
  // the option so the bar states the filter that is actually applied — an empty
  // result under a named owner is the true answer, not a broken control.
  const owners = useMemo(() => {
    const found = uniq(ofKind.map(e => shortRef(e.spec?.owner)));
    return owner !== ANY && !found.includes(owner)
      ? [...found, owner].sort()
      : found;
  }, [ofKind, owner]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ofKind
      .filter(e => (type === ANY ? true : str(e.spec?.type) === type))
      .filter(e =>
        lifecycle === ANY ? true : str(e.spec?.lifecycle) === lifecycle,
      )
      .filter(e => (owner === ANY ? true : shortRef(e.spec?.owner) === owner))
      .filter(e => {
        if (scope === MINE)
          return myRefs.includes(fullRef(e.spec?.owner, 'Group'));
        if (scope === STARRED) return isStarredEntity(e);
        return true;
      })
      .filter(e =>
        q
          ? `${e.metadata.title ?? ''} ${e.metadata.name} ${
              e.metadata.description ?? ''
            }`
              .toLowerCase()
              .includes(q)
          : true,
      )
      .sort((a, b) =>
        (a.metadata.title ?? a.metadata.name).localeCompare(
          b.metadata.title ?? b.metadata.name,
        ),
      );
  }, [ofKind, type, lifecycle, owner, scope, myRefs, query, isStarredEntity]);

  const loading = !entities && !error;

  const selectProps = {
    select: true,
    variant: 'outlined' as const,
    size: 'small' as const,
  };

  return (
    <Page themeId="home">
      <Header title="Catalog" subtitle="Everything we build and run" />
      <Content>
        {loading && <Progress />}
        {error && <ResponseErrorPanel error={error} />}

        {entities && (
          <>
            <div className={classes.filterBar}>
              <TextField
                className={classes.search}
                variant="outlined"
                size="small"
                placeholder="Search"
                value={query}
                onChange={e => setQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                {...selectProps}
                className={classes.select}
                label="Show"
                value={scope}
                onChange={e => setScope(e.target.value)}
              >
                <MenuItem value={ANY}>All</MenuItem>
                <MenuItem value={MINE}>Owned by me or my team</MenuItem>
                <MenuItem value={STARRED}>Starred</MenuItem>
              </TextField>

              <TextField
                {...selectProps}
                className={classes.select}
                label="Kind"
                value={kind}
                onChange={e => {
                  setKind(e.target.value);
                  setType(ANY);
                  setLifecycle(ANY);
                  setOwner(ANY);
                }}
              >
                <MenuItem value={ANY}>All kinds</MenuItem>
                {kinds.map(k => (
                  <MenuItem key={k} value={k}>
                    {k}
                  </MenuItem>
                ))}
              </TextField>

              {types.length > 0 && (
                <TextField
                  {...selectProps}
                  className={classes.select}
                  label="Type"
                  value={type}
                  onChange={e => setType(e.target.value)}
                >
                  <MenuItem value={ANY}>All types</MenuItem>
                  {types.map(t => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {lifecycles.length > 0 && (
                <TextField
                  {...selectProps}
                  className={classes.select}
                  label="Lifecycle"
                  value={lifecycle}
                  onChange={e => setLifecycle(e.target.value)}
                >
                  <MenuItem value={ANY}>All lifecycles</MenuItem>
                  {lifecycles.map(l => (
                    <MenuItem key={l} value={l}>
                      {l}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {owners.length > 0 && (
                <TextField
                  {...selectProps}
                  className={classes.select}
                  label="Owner"
                  value={owner}
                  onChange={e => setOwner(e.target.value)}
                >
                  <MenuItem value={ANY}>All owners</MenuItem>
                  {owners.map(o => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              <span className={classes.count}>
                {visible.length} {visible.length === 1 ? 'result' : 'results'}
              </span>
            </div>

            {visible.length === 0 ? (
              <EmptyState
                missing="content"
                title="Nothing matches"
                description="Try a different search or clear the filters."
              />
            ) : (
              <Grid container spacing={2}>
                {visible.map(entity => {
                  const name = entity.metadata.name;
                  const ns = entity.metadata.namespace ?? 'default';
                  const title = entity.metadata.title ?? name;
                  const desc = entity.metadata.description ?? '';
                  const eType = str(entity.spec?.type);
                  const eLife = str(entity.spec?.lifecycle);
                  const eOwner = shortRef(entity.spec?.owner);
                  const eSystem = shortRef(entity.spec?.system);
                  const starred = isStarredEntity(entity);
                  return (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3}
                      key={`${entity.kind}/${ns}/${name}`}
                    >
                      <div className={classes.cardWrap}>
                        <Tooltip
                          title={starred ? 'Unstar' : 'Star'}
                          placement="top"
                        >
                          <IconButton
                            size="small"
                            aria-label={starred ? 'Unstar' : 'Star'}
                            className={`${classes.star} ${
                              starred ? classes.starOn : ''
                            }`}
                            onClick={ev => {
                              // The card is a link; don't navigate on star.
                              ev.preventDefault();
                              ev.stopPropagation();
                              toggleStarredEntity(entity);
                            }}
                          >
                            {starred ? (
                              <StarIcon fontSize="small" />
                            ) : (
                              <StarBorderIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Link
                          to={`/catalog/${ns}/${entity.kind.toLowerCase()}/${name}`}
                          className={classes.cardLink}
                        >
                          <Card className={classes.card}>
                            <CardContent className={classes.cardBody}>
                              {/* With a kind pinned, the specific type
                                ("service", "website") is the more useful label
                                and the kind is redundant. Under "All kinds"
                                that reverses: the grid mixes kinds, and type
                                alone cannot tell a Component from a Group — so
                                the kind leads, with the type after it. */}
                              <span className={classes.kindRow}>
                                {kind === ANY
                                  ? [entity.kind, eType]
                                      .filter(Boolean)
                                      .join(' · ')
                                  : eType || entity.kind}
                              </span>
                              <Typography className={classes.name}>
                                {title}
                              </Typography>
                              {desc && (
                                <Typography className={classes.desc}>
                                  {desc}
                                </Typography>
                              )}
                              {/* Type moved to the header label above, so only
                                lifecycle remains as a chip. */}
                              {eLife && (
                                <Box className={classes.meta}>
                                  <Chip size="small" label={eLife} />
                                </Box>
                              )}
                              {(eSystem || eOwner) && (
                                <div className={classes.footer}>
                                  {eSystem && (
                                    <div className={classes.footerCell}>
                                      <span className={classes.footerLabel}>
                                        System
                                      </span>
                                      <span className={classes.footerValue}>
                                        {eSystem}
                                      </span>
                                    </div>
                                  )}
                                  {eOwner && (
                                    <div className={classes.footerCell}>
                                      <span className={classes.footerLabel}>
                                        Team
                                      </span>
                                      <span className={classes.footerValue}>
                                        {eOwner}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        </Link>
                      </div>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </>
        )}
      </Content>
    </Page>
  );
};
